// src/components/onboarding/PolicyAcceptanceModal.jsx — Executive Document Reader & Policy Acceptance
import React, { useState, useEffect, useMemo } from "react";
import { Shield, Check, ArrowRight, ArrowLeft, AlertTriangle, FileText, CheckCircle2 } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";
import "./onboarding.css";

const DEFAULT_POLICIES = [
  {
    policy_key: "terms",
    title: "Terms & Conditions of CRM Usage",
    version: "1.0",
    summary: "Authorized enterprise business use only. Truthful recordkeeping required. Activity monitoring, transaction ledgers, and audit logs are continuously active.",
    content: "Platinum CRM is a proprietary enterprise workspace provided strictly for authorized company business purposes. All employees, managers, and administrators must utilize this platform in full accordance with established company procedures, professional standards, and legal requirements. Users must provide accurate, truthful, and verified records at all times. Misuse of CRM functionality, unauthorized data transmission, or conducting personal commercial activities using company resources is strictly prohibited.\n\nThe company reserves the right to monitor CRM activities, inspect transaction ledgers, review interaction history, and audit system logs for legitimate business, security, operational, and regulatory compliance purposes. Any violation of these terms will result in immediate revocation of access and disciplinary action up to and including termination of employment and legal recourse."
  },
  {
    policy_key: "privacy",
    title: "Privacy Policy & Customer Data Protection",
    version: "1.0",
    summary: "Customer names, WhatsApp contacts, vehicle preferences, and IDs are strictly confidential. Sharing with external brokers, unauthorized peers, or third parties is legally forbidden.",
    content: "Platinum CRM stores sensitive, highly confidential customer and business data including names, telephone numbers, WhatsApp contact records, email addresses, vehicle purchasing preferences, financial transactions, passport copies, identification documents, and commercial invoices. This information is gathered solely to facilitate legitimate automotive export, sales fulfillment, shipping logistics, and customer relationship management.\n\nAccess to customer data is strictly governed by role-based authorization: agents may only access leads assigned to them; managers may oversee team operations; administrators govern system-wide compliance. Customer data must never be disclosed to third parties, external brokers, marketing agencies, or unauthorized colleagues. All employees are legally and contractually obligated to safeguard customer confidentiality and report any suspected privacy breaches immediately to company administration."
  },
  {
    policy_key: "lead_confidentiality",
    title: "Lead Confidentiality, Ownership & Anti-Theft Policy",
    version: "1.0",
    summary: "CRITICAL NOTICE: ALL LEADS AND NEGOTIATIONS ARE 100% EXCLUSIVE COMPANY PROPERTY. Exporting, photographing, retaining, or diverting company leads constitutes criminal theft of trade secrets.",
    content: "ALL LEADS, CUSTOMER INQUIRIES, NEGOTIATIONS, AND CLIENT CONTACTS ENTERED INTO OR GENERATED BY PLATINUM CRM ARE THE EXCLUSIVE, PERMANENT BUSINESS PROPERTY OF THE COMPANY. Under no circumstances does an employee, agent, or manager acquire personal ownership or proprietary rights over any lead or customer.\n\nEmployees are strictly prohibited from copying, exporting, photographing, downloading, transferring, or memorizing customer contact details for personal gain, private brokerage, or competitor advantage. Diverting company leads to third parties or attempting to conduct vehicle transactions outside official company bank accounts constitutes criminal misappropriation, corporate espionage, and theft of trade secrets.\n\nEmployees must not deliberately conceal, delete, misrepresent, or manipulate lead remarks, statuses, or assignment timestamps. Lead assignments, reassignments, and protection locks are automated and governed by company management; tampering with or attempting to circumvent assignment algorithms is forbidden. The obligation of lead confidentiality remains permanently binding during and after termination of employment."
  },
  {
    policy_key: "information_security",
    title: "Information Security & Account Integrity Policy",
    version: "1.0",
    summary: "Credentials must never be shared. Unattended sessions are prohibited. Uploading CRM customer data or invoices to external AI tools or personal storage is strictly forbidden.",
    content: "Account integrity is vital to safeguarding company assets and commercial operations. Every user is individually accountable for all actions performed under their credentials. Passwords must meet company complexity standards (minimum 8 characters, uppercase and lowercase letters, numbers, and special symbols) and must never be shared with colleagues, supervisors, or third parties.\n\nEmployees must never leave an authenticated CRM session unattended on any workstation. Always log out when stepping away or utilizing shared computers. Unauthorized access attempts, permission bypassing, SQL injection, API tampering, and installing unapproved browser extensions or screen-capture tools are severe security violations.\n\nDo not copy, paste, or upload confidential customer records, invoices, or business leads into public external AI tools, unauthorized third-party platforms, or personal cloud storage. Any detected vulnerability, unauthorized access, or account compromise must be reported immediately."
  },
  {
    policy_key: "responsibilities",
    title: "Employee Responsibilities & Professional Conduct",
    version: "1.0",
    summary: "Follow-ups must be executed punctually. All customer interaction remarks must be logged truthfully without falsification. Courtesy and professional excellence are mandatory.",
    content: "Every authorized Platinum CRM user carries an obligation to maintain professional excellence, diligent record-keeping, and ethical customer engagement. Follow-ups must be conducted punctually and documented truthfully in lead remarks. Agents must refrain from creating fictitious leads, duplicate entries, or falsified interaction notes.\n\nManagers are responsible for equitable lead distribution, monitoring agent response times, and auditing follow-up quality. All communication conducted through CRM tools, email center, WhatsApp integrations, and team chat channels must remain courteous, professional, and aligned with company brand standards. Employees must report technical malfunctions, system anomalies, or suspicious account behaviors promptly to technical administrators."
  }
];

export default function PolicyAcceptanceModal({ user, onSuccess }) {
  const [policies, setPolicies] = useState(DEFAULT_POLICIES);
  const [activeIndex, setActiveIndex] = useState(0);

  // Map of accepted policy keys
  const [agreedMap, setAgreedMap] = useState({
    terms: false,
    privacy: false,
    lead_confidentiality: false,
    information_security: false,
    responsibilities: false
  });

  const [finalConfirmation, setFinalConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/onboarding/policies")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setPolicies(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const activePolicy = policies[activeIndex] || policies[0];
  const isLeadPolicy = activePolicy.policy_key === "lead_confidentiality";
  const isActiveAgreed = Boolean(agreedMap[activePolicy.policy_key]);

  const agreedCount = useMemo(() => {
    return Object.values(agreedMap).filter(Boolean).length;
  }, [agreedMap]);

  const allFiveAgreed = agreedCount === 5;
  const canSubmit = allFiveAgreed && finalConfirmation;

  const toggleActivePolicyAgreed = () => {
    setAgreedMap((prev) => ({
      ...prev,
      [activePolicy.policy_key]: !prev[activePolicy.policy_key]
    }));
  };

  const handleSelectAll = () => {
    const nextState = !allFiveAgreed;
    setAgreedMap({
      terms: nextState,
      privacy: nextState,
      lead_confidentiality: nextState,
      information_security: nextState,
      responsibilities: nextState
    });
    if (nextState) {
      setFinalConfirmation(true);
    }
  };

  const handleNextPolicy = () => {
    if (activeIndex < policies.length - 1) {
      setActiveIndex((prev) => prev + 1);
    }
  };

  const handlePrevPolicy = () => {
    if (activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) {
      if (!allFiveAgreed) {
        toast.error(`Please review and agree to all 5 policies (${5 - agreedCount} remaining).`);
      } else {
        toast.error("Please check the final solemn declaration to proceed.");
      }
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/onboarding/accept-policies", {
        termsAccepted: true,
        privacyAccepted: true,
        leadPolicyAccepted: true,
        securityPolicyAccepted: true,
        responsibilitiesAccepted: true,
        finalConfirmation: true
      });

      toast.success("Policies accepted successfully. Welcome to Platinum CRM!");
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record policy acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPercent = Math.round((agreedCount / 5) * 100);

  return (
    <div className="onboarding-reader-shell">
      {/* ── Top Header Bar ── */}
      <div className="onboarding-reader-topbar">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span className="onboarding-badge-role">
              <Shield size={13} /> MANDATORY CORPORATE GOVERNANCE
            </span>
            <span style={{ fontSize: "12px", color: "var(--muted)" }}>
              User: <strong style={{ color: "#ffffff" }}>{user?.name || "Team Member"}</strong> ({user?.role || "Agent"})
            </span>
          </div>
          <h2 className="onboarding-title">Company Policies &amp; Lead Ownership Agreement</h2>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: "12.5px", color: "#94a3b8" }}>
            Reviewed: <strong style={{ color: allFiveAgreed ? "#30d158" : "#38bdf8" }}>{agreedCount} of 5</strong>
          </span>
          <button
            type="button"
            onClick={handleSelectAll}
            style={{
              background: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              color: "#38bdf8",
              borderRadius: "8px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            {allFiveAgreed ? "Deselect All" : "Quick Agree to All"}
          </button>
        </div>
      </div>

      {/* ── Split Layout: Left Navigation + Right Document Reader ── */}
      <div className="onboarding-split-body">
        {/* Left Sidebar */}
        <div className="onboarding-sidebar-pane">
          {/* Progress Header */}
          <div className="onboarding-progress-header">
            <div className="onboarding-progress-labels">
              <span>Policy Acceptance Progress</span>
              <span style={{ color: allFiveAgreed ? "#30d158" : "#38bdf8" }}>{progressPercent}%</span>
            </div>
            <div className="onboarding-progress-bar-track">
              <div
                className="onboarding-progress-bar-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* 5 Policy Selector Tabs */}
          <div className="onboarding-policy-list">
            {policies.map((p, idx) => {
              const isActive = idx === activeIndex;
              const isAgreed = Boolean(agreedMap[p.policy_key]);
              const isLead = p.policy_key === "lead_confidentiality";

              return (
                <div
                  key={p.policy_key}
                  className={`onboarding-policy-tab-item ${isActive ? "active" : ""} ${isAgreed ? "is-agreed" : ""}`}
                  onClick={() => setActiveIndex(idx)}
                >
                  <div className="onboarding-tab-info">
                    <div className={`onboarding-tab-check-icon ${isAgreed ? "checked" : ""}`}>
                      {isAgreed ? <Check size={14} strokeWidth={3} /> : <FileText size={13} />}
                    </div>
                    <div>
                      <div className="onboarding-tab-title">{p.title}</div>
                      <div style={{ display: "flex", alignItems: "center", marginTop: 2 }}>
                        <span className="onboarding-tab-tag">v{p.version || "1.0"}</span>
                        {isLead && (
                          <span className="onboarding-tab-tag red">100% Company Property</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sidebar Bottom: Final Declaration & Master Submit */}
          <div className="onboarding-sidebar-bottom">
            <div
              className={`onboarding-final-dec-box ${canSubmit ? "active-ready" : ""}`}
              onClick={() => {
                if (allFiveAgreed) {
                  setFinalConfirmation((prev) => !prev);
                } else {
                  toast.error("Please review all 5 policies first.");
                }
              }}
            >
              <div
                className={`onboarding-custom-checkbox ${finalConfirmation ? "checked" : ""}`}
                style={{
                  opacity: allFiveAgreed ? 1 : 0.4,
                  cursor: allFiveAgreed ? "pointer" : "not-allowed"
                }}
              >
                {finalConfirmation && <Check size={15} strokeWidth={3} />}
              </div>
              <p
                className="onboarding-final-dec-text"
                style={{ opacity: allFiveAgreed ? 1 : 0.5 }}
              >
                <strong>Final Solemn Declaration:</strong> I solemnly declare and acknowledge that all leads, negotiations, and customer contacts are 100% exclusive company property. I unconditionally accept all corporate policies.
              </p>
            </div>

            <button
              type="button"
              className="onboarding-submit-btn-full"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? (
                <span>Recording Verification...</span>
              ) : canSubmit ? (
                <>
                  <CheckCircle2 size={18} /> Accept Policies &amp; Enter CRM
                </>
              ) : !allFiveAgreed ? (
                <span style={{ fontSize: "12.5px" }}>
                  {5 - agreedCount} {5 - agreedCount === 1 ? "Policy" : "Policies"} Remaining
                </span>
              ) : (
                <span style={{ fontSize: "12.5px" }}>
                  Check Final Declaration to Unlock
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Right Pane: Large Comfortable Document Reader */}
        <div className="onboarding-doc-pane">
          <div className="onboarding-doc-content-scroll">
            <div className="onboarding-doc-header-row">
              <div>
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#38bdf8", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
                  POLICY {activeIndex + 1} OF {policies.length}
                </span>
                <h3 className="onboarding-doc-title">{activePolicy.title}</h3>
              </div>
              <span className="onboarding-doc-badge-official">
                Official Corporate Policy v{activePolicy.version || "1.0"}
              </span>
            </div>

            {/* Highlights / Summary Callout Box */}
            {activePolicy.summary && (
              <div className={`onboarding-doc-highlight-box ${isLeadPolicy ? "lead-warning" : ""}`}>
                <div className="onboarding-doc-highlight-title">
                  {isLeadPolicy ? (
                    <>
                      <AlertTriangle size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />
                      Strict Anti-Theft &amp; Exclusive Ownership Notice
                    </>
                  ) : (
                    "Executive Summary & Core Mandate"
                  )}
                </div>
                <p className="onboarding-doc-highlight-text">{activePolicy.summary}</p>
              </div>
            )}

            {/* Full Legal Text with Generous Typography */}
            <div className="onboarding-doc-body-text">
              {activePolicy.content.split("\n\n").map((para, i) => (
                <p key={i} style={{ marginBottom: 16 }}>{para}</p>
              ))}
            </div>
          </div>

          {/* Reader Bottom Bar */}
          <div className="onboarding-doc-bottom-bar">
            {/* Agreement Checkbox for Current Active Policy */}
            <div
              className="onboarding-doc-agree-toggle"
              onClick={toggleActivePolicyAgreed}
            >
              <div className={`onboarding-custom-checkbox ${isActiveAgreed ? "checked" : ""}`}>
                {isActiveAgreed && <Check size={16} strokeWidth={3} />}
              </div>
              <span className="onboarding-doc-agree-label">
                I have thoroughly read and accept the <strong>{activePolicy.title}</strong>
              </span>
            </div>

            {/* Prev / Next Navigation */}
            <div className="onboarding-reader-nav-btns">
              <button
                type="button"
                className="onboarding-btn-nav prev"
                onClick={handlePrevPolicy}
                disabled={activeIndex === 0}
              >
                <ArrowLeft size={14} /> Previous
              </button>

              {activeIndex < policies.length - 1 ? (
                <button
                  type="button"
                  className="onboarding-btn-nav next"
                  onClick={handleNextPolicy}
                >
                  Next Policy <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  className="onboarding-btn-nav next"
                  onClick={() => {
                    if (!isActiveAgreed) toggleActivePolicyAgreed();
                    if (allFiveAgreed) setFinalConfirmation(true);
                  }}
                >
                  <Check size={14} /> Done Reviewing
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
